import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Alert } from '@mui/material'
import SHA256 from 'crypto-js/sha256'
import { useIntl } from 'react-intl'

import { BoardTheme } from './boardTheme'
import buildSandboxDocument, { createNonce, DASHBOARD_SANDBOX } from './buildSandboxDocument'
import {
  BLOCK_FRAME_MAX_HEIGHT,
  firstFrameHeight,
  followFrameHeight,
  FRAME_HEIGHT_MESSAGE,
  FRAME_SETTLE_MS,
  heightReporter,
  PAGE_FRAME_MAX_HEIGHT,
  settleFrameHeight,
} from './frameHeight'
import useDashboardBridge from './useDashboardBridge'

type Props = {
  html: string
  payload?: unknown
  title: string
  // What the frame shows: one block's page, or a whole version's page, which may grow taller.
  frame?: 'block' | 'page'
  sizing?: 'content' | 'viewport'
  // The viewer's theme. The frame is built in the one it has when the frame mounts; a later switch goes to it
  // on its port, since a new srcdoc would load the frame again, and the load guard would stop it.
  theme?: BoardTheme
  // Called once when the frame is stopped for loading a second time.
  onStop?: () => void
}

export const dashboardHtmlKey = (html: string): string => SHA256(html).toString()

// Each html gets a frame of its own, so switching versions or blocks starts from a fresh frame with its own
// load count, token and nonce.
export default function SandboxedDashboard(props: Props) {
  const frameKey = useMemo(() => dashboardHtmlKey(props.html), [props.html])

  return <SandboxFrame key={frameKey} {...props} />
}

// The frame is as tall as its page says, between the limits in frameHeight, and never scrolls itself: the board
// page does. A height counts only from this frame's window, with this frame's token. A page that keeps growing
// because its frame did is held, as frameHeight says.
function SandboxFrame({ html, payload, title, frame = 'page', sizing = 'content', theme, onStop }: Props) {
  const intl = useIntl()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const loads = useRef(0)
  const [stopped, setStopped] = useState(false)
  const [frameHeight, setFrameHeight] = useState(() => firstFrameHeight(Date.now()))
  const maxHeight = frame === 'block' ? BLOCK_FRAME_MAX_HEIGHT : PAGE_FRAME_MAX_HEIGHT
  const currentTheme = useRef(theme)
  currentTheme.current = theme

  // Built once per frame, so the nonce, the token and the srcdoc stay the same across re-renders, a theme
  // switch included.
  const { srcDoc, token } = useMemo(() => {
    const frameToken = createNonce()

    return {
      token: frameToken,
      srcDoc: buildSandboxDocument(html, {
        probe: sizing === 'content' ? heightReporter({ token: frameToken }) : undefined,
        theme: currentTheme.current,
      }),
    }
  }, [html, sizing])

  useDashboardBridge(iframeRef, payload, { enabled: !stopped, theme })

  // A layout effect, like the bridge's, so the listener is in place before the frame can load.
  useLayoutEffect(() => {
    if (stopped || sizing === 'viewport') return undefined

    const onMessage = (event: MessageEvent) => {
      const frameWindow = iframeRef.current?.contentWindow
      if (!frameWindow || event.source !== frameWindow) return
      if (event.data?.type !== FRAME_HEIGHT_MESSAGE || event.data.token !== token) return

      const reported = event.data.height
      const now = Date.now()
      setFrameHeight(current => followFrameHeight(current, reported, now, maxHeight))
    }

    window.addEventListener('message', onMessage)

    return () => window.removeEventListener('message', onMessage)
  }, [token, stopped, maxHeight, sizing])

  // A held frame tries its page's latest height once the page has been quiet for a while.
  useEffect(() => {
    if (!frameHeight.hold || frameHeight.hold.forGood) return undefined

    const timer = window.setTimeout(
      () => setFrameHeight(current => settleFrameHeight(current, Date.now())),
      FRAME_SETTLE_MS,
    )

    return () => window.clearTimeout(timer)
  }, [frameHeight])

  // The srcdoc load is the only one expected. Any later load means the page navigated itself, so it gets no
  // more data and is taken off the page.
  const onLoad = () => {
    loads.current += 1
    if (loads.current !== 2) return

    setStopped(true)
    onStop?.()
  }

  if (stopped) {
    return (
      <Alert severity='error' data-cy='dashboardStopped'>
        {intl.formatMessage({ id: 'sparckDashboards.viewer.stopped' })}
      </Alert>
    )
  }

  return (
    <iframe
      ref={iframeRef}
      sandbox={DASHBOARD_SANDBOX}
      srcDoc={srcDoc}
      title={title}
      onLoad={onLoad}
      scrolling={sizing === 'viewport' ? 'auto' : 'no'}
      data-cy='dashboardFrame'
      style={{ display: 'block', width: '100%', height: sizing === 'viewport' ? '100%' : frameHeight.height, border: 0, overflow: 'hidden' }}
    />
  )
}
